import { useEffect } from 'react'
import { create } from 'zustand'
import { fetchGeneralToolResults } from '../../api/generalAgentChatApi'
import type { GeneralChatMessage } from '../../types'
import { cardsOfRows, type ChatCard } from './cardResults'

interface CardResultsStore {
    /** The chat the cards belong to. */
    tabId: string | null
    cards: ChatCard[]
    /** Reads the tab's card results again; a slower read for a chat no longer open is dropped. */
    load: (tabId: string | null) => Promise<void>
}

let reads = 0
const NO_CARDS: ChatCard[] = []

/**
 * The cards read now, keeping each one already held by its id (a stored card result never changes), and the held list itself
 * when nothing was added: a turn's end then re-renders only its new cards, not every table in the chat.
 */
export function keepCardIdentity(held: ChatCard[], read: ChatCard[]): ChatCard[] {
    const byId = new Map(held.map((card) => [card.id, card]))
    const next = read.map((card) => byId.get(card.id) ?? card)
    return next.length === held.length && next.every((card, i) => card === held[i]) ? held : next
}

/**
 * The open chat's card results (plan FR-G3), shared by the thread that draws them and the composer that offers their
 * loads as upload targets. Cards add to the conversation, so a failed read keeps what is shown.
 */
export const useCardResultsStore = create<CardResultsStore>((set, get) => ({
    tabId: null,
    cards: [],
    load: async (tabId) => {
        const mine = ++reads
        if (tabId !== get().tabId) set({ tabId, cards: [] })
        if (!tabId) return
        try {
            const rows = await fetchGeneralToolResults(tabId)
            if (mine === reads) set((s) => ({ cards: keepCardIdentity(s.cards, cardsOfRows(rows)) }))
        } catch {
            // the thread still shows the agent's own words
        }
    },
}))

/** Keeps the card results in step with the chat: read again whenever its stored messages change (a turn ended). */
export const useThreadCards = (tabId: string | null, messages: GeneralChatMessage[]): ChatCard[] => {
    const load = useCardResultsStore((s) => s.load)
    const cards = useCardResultsStore((s) => (s.tabId === tabId ? s.cards : NO_CARDS))
    // Stored rows only: a local bubble just sent does not mean a tool has answered.
    const storedKey = messages
        .filter((m) => !m.id.startsWith('local-'))
        .map((m) => m.id)
        .join(',')
    useEffect(() => {
        void load(tabId)
    }, [load, tabId, storedKey])
    return cards
}
